/**
 * Key Findings Component
 * Shows the top accessibility issues prioritized for remediation
 */

import { useState, useMemo } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  ExternalLink,
} from 'lucide-react';
import { PageResult, AccessibilityIssue } from '../../types';

interface AffectedPage {
  url: string;
  nodes: { html: string; target: string[]; failureSummary?: string }[];
}

interface KeyFinding {
  rank: number;
  ruleId: string;
  title: string;
  severity: string;
  occurrences: number;
  wcagCriteria?: string[];
  description: string;
  helpUrl?: string;
  affectedPages: AffectedPage[];
}

interface KeyFindingsProps {
  pageResults: PageResult[];
  maxFindings?: number;
}

// Mapping of rule IDs to human-readable titles
const RULE_TITLES: Record<string, string> = {
  'image-alt': 'Images must have alternate text',
  'link-name': 'Links must have discernible text',
  'button-name': 'Buttons must have discernible text',
  'label': 'Form elements must have labels',
  'color-contrast': 'Elements must have sufficient color contrast',
  'heading-order': 'Heading levels should increase by one',
  'html-has-lang': 'HTML element must have a lang attribute',
  'document-title': 'Documents must have a title element',
  'bypass': 'Page must have means to bypass repeated blocks',
  'meta-viewport': 'Zooming and scaling must not be disabled',
  'frame-title': 'Frames must have an accessible name',
  'form-field-multiple-labels': 'Form fields should not have multiple labels',
  'select-name': 'Select element must have an accessible name',
  'input-button-name': 'Input buttons must have discernible text',
  'input-image-alt': 'Image buttons must have alternate text',
  'tabindex': 'Elements should not have tabindex greater than zero',
  'aria-allowed-attr': 'ARIA attributes must be allowed for element role',
  'aria-hidden-focus': 'ARIA hidden elements must not be focusable',
  'aria-required-attr': 'Required ARIA attributes must be provided',
  'aria-valid-attr-value': 'ARIA attributes must have valid values',
};

const PAGES_PER_BATCH = 10;

function truncateHtml(html: string, maxLength = 150): string {
  const trimmed = html.replace(/\s+/g, ' ').trim();
  if (trimmed.length <= maxLength) return trimmed;
  return trimmed.substring(0, maxLength) + '...';
}

function ExpandableFinding({ finding }: { finding: KeyFinding }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [visiblePages, setVisiblePages] = useState(PAGES_PER_BATCH);
  const [copiedSelector, setCopiedSelector] = useState<string | null>(null);

  const handleCopySelector = async (selector: string) => {
    try {
      await navigator.clipboard.writeText(selector);
      setCopiedSelector(selector);
      setTimeout(() => setCopiedSelector(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'serious':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'moderate':
        return <Info className="w-5 h-5 text-amber-500" />;
      default:
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    }
  };

  const getSeverityBadgeClass = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-700';
      case 'serious':
        return 'bg-orange-100 text-orange-700';
      case 'moderate':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-green-100 text-green-700';
    }
  };

  const displayedPages = finding.affectedPages.slice(0, visiblePages);
  const hasMorePages = finding.affectedPages.length > visiblePages;
  const totalElements = finding.affectedPages.reduce((sum, p) => sum + p.nodes.length, 0);

  return (
    <div className="card border border-gray-200 hover:border-coral/30 transition-colors overflow-hidden">
      {/* Header - Clickable to expand */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left"
      >
        <div className="flex items-start gap-4">
          {/* Rank Number */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-dark-navy text-white flex items-center justify-center font-semibold text-body-sm">
            {finding.rank}
          </div>

          {/* Content */}
          <div className="flex-grow min-w-0">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="flex items-center gap-2">
                {getSeverityIcon(finding.severity)}
                <h3 className="font-semibold text-dark-navy">{finding.title}</h3>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`px-2 py-1 rounded-full text-xs font-medium uppercase ${getSeverityBadgeClass(finding.severity)}`}>
                  {finding.severity}
                </span>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-medium" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-medium" />
                )}
              </div>
            </div>

            <p className="text-body-sm text-gray-dark mb-2">{finding.description}</p>

            <div className="flex flex-wrap items-center gap-4 text-body-sm text-gray-medium">
              <span className="flex items-center gap-1">
                <span className="font-medium">{finding.occurrences}</span>
                {finding.occurrences === 1 ? 'occurrence' : 'occurrences'}
                {' across '}
                <span className="font-medium">{finding.affectedPages.length}</span>
                {finding.affectedPages.length === 1 ? ' page' : ' pages'}
              </span>
              <span className="flex items-center gap-1">
                <span className="font-medium">{totalElements}</span>
                {totalElements === 1 ? ' element' : ' elements'}
              </span>
              {finding.wcagCriteria && finding.wcagCriteria.length > 0 && (
                <span className="text-coral font-medium">
                  WCAG {finding.wcagCriteria.join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-light animate-in slide-in-from-top-2 duration-200">
          {/* Help URL */}
          {finding.helpUrl && (
            <a
              href={finding.helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-body-sm text-coral hover:text-coral/80 mb-4"
            >
              Learn more about this issue
              <ExternalLink size={14} />
            </a>
          )}

          {/* Affected Pages */}
          <div className="space-y-4">
            <h4 className="text-body-sm font-semibold text-dark-navy">
              Affected Pages ({finding.affectedPages.length})
            </h4>

            {displayedPages.map((page, pageIndex) => (
              <div key={pageIndex} className="bg-gray-light/50 rounded-lg p-3">
                <div className="text-body-sm font-medium text-dark-navy mb-2 truncate">
                  {page.url}
                </div>

                {/* Elements on this page */}
                <div className="space-y-2">
                  {page.nodes.slice(0, 5).map((node, nodeIndex) => (
                    <div key={nodeIndex} className="bg-white rounded p-2 border border-gray-200">
                      {/* Selector */}
                      {node.target && node.target.length > 0 && (
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <code className="text-caption font-mono text-coral truncate flex-1">
                            {node.target.join(' > ')}
                          </code>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopySelector(node.target.join(' > '));
                            }}
                            className="flex items-center gap-1 px-2 py-0.5 text-caption text-gray-medium hover:text-coral rounded border border-gray-200 hover:border-coral transition-colors flex-shrink-0"
                            title="Copy selector"
                          >
                            {copiedSelector === node.target.join(' > ') ? (
                              <>
                                <Check size={10} className="text-status-success" />
                                <span className="text-status-success">Copied</span>
                              </>
                            ) : (
                              <>
                                <Copy size={10} />
                                <span>Copy</span>
                              </>
                            )}
                          </button>
                        </div>
                      )}

                      {/* HTML snippet */}
                      <pre className="text-caption text-gray-600 overflow-x-auto whitespace-pre-wrap break-all">
                        {truncateHtml(node.html)}
                      </pre>

                      {/* Failure summary */}
                      {node.failureSummary && (
                        <p className="text-caption text-gray-medium mt-1 italic">
                          {node.failureSummary}
                        </p>
                      )}
                    </div>
                  ))}

                  {page.nodes.length > 5 && (
                    <p className="text-caption text-gray-medium">
                      + {page.nodes.length - 5} more element(s) on this page
                    </p>
                  )}
                </div>
              </div>
            ))}

            {/* Load More Pagination */}
            {hasMorePages && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setVisiblePages(prev => prev + PAGES_PER_BATCH);
                }}
                className="w-full py-2 text-body-sm text-coral hover:text-coral/80 border border-coral/30 rounded-lg hover:bg-coral/5 transition-colors"
              >
                Show {Math.min(PAGES_PER_BATCH, finding.affectedPages.length - visiblePages)} more pages
                ({finding.affectedPages.length - visiblePages} remaining)
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function KeyFindings({ pageResults, maxFindings = 10 }: KeyFindingsProps) {
  // Extract and aggregate key findings with affected pages
  const keyFindings = useMemo(() => {
    // Group issues by ruleId across all pages
    const issueGroups = new Map<string, {
      issue: AccessibilityIssue;
      count: number;
      affectedPages: Map<string, AffectedPage>;
    }>();

    pageResults.forEach((page) => {
      (page.accessibilityIssues || []).forEach((issue) => {
        const existing = issueGroups.get(issue.ruleId);
        if (existing) {
          existing.count++;
          // Add nodes to existing page or create new page entry
          const pageEntry = existing.affectedPages.get(page.pageUrl);
          if (pageEntry) {
            pageEntry.nodes.push(...issue.nodes);
          } else {
            existing.affectedPages.set(page.pageUrl, {
              url: page.pageUrl,
              nodes: [...issue.nodes],
            });
          }
        } else {
          const affectedPages = new Map<string, AffectedPage>();
          affectedPages.set(page.pageUrl, {
            url: page.pageUrl,
            nodes: [...issue.nodes],
          });
          issueGroups.set(issue.ruleId, { issue, count: 1, affectedPages });
        }
      });
    });

    // Convert to array and sort by severity and count
    const sortedIssues = Array.from(issueGroups.values()).sort((a, b) => {
      const severityOrder: Record<string, number> = { critical: 0, serious: 1, moderate: 2, minor: 3 };
      const severityDiff = (severityOrder[a.issue.impact] || 4) - (severityOrder[b.issue.impact] || 4);
      if (severityDiff !== 0) return severityDiff;
      return b.count - a.count;
    });

    // Create key findings
    return sortedIssues.slice(0, maxFindings).map((item, idx): KeyFinding => ({
      rank: idx + 1,
      ruleId: item.issue.ruleId,
      title: RULE_TITLES[item.issue.ruleId] || item.issue.description.substring(0, 60),
      severity: item.issue.impact,
      occurrences: item.count,
      wcagCriteria: item.issue.wcagCriteria,
      description: item.issue.description,
      helpUrl: item.issue.helpUrl,
      affectedPages: Array.from(item.affectedPages.values()),
    }));
  }, [pageResults, maxFindings]);

  // Summary stats
  const summary = useMemo(() => {
    const criticalCount = keyFindings.filter((f) => f.severity === 'critical').length;
    const seriousCount = keyFindings.filter((f) => f.severity === 'serious').length;
    const moderateCount = keyFindings.filter((f) => f.severity === 'moderate').length;
    return { criticalCount, seriousCount, moderateCount };
  }, [keyFindings]);

  const totalIssues = pageResults.reduce((sum, p) => sum + (p.accessibilityIssues?.length || 0), 0);

  if (keyFindings.length === 0) {
    return (
      <div className="card bg-green-50 border border-green-200">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
          <div>
            <h3 className="font-semibold text-green-800">No Accessibility Issues Found</h3>
            <p className="text-body-sm text-green-700">
              Great work! Your website has passed all accessibility checks.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div>
        <h2 className="text-heading-2 font-semibold text-dark-navy mb-2">Key Findings</h2>
        <p className="text-body text-gray-medium">
          The scan identified {totalIssues} total issues across {pageResults.length} pages.
          Click on any finding to see affected pages and elements.
        </p>
      </div>

      {/* Findings List */}
      <div className="space-y-3">
        {keyFindings.map((finding) => (
          <ExpandableFinding key={finding.ruleId} finding={finding} />
        ))}
      </div>

      {/* Summary Stats */}
      {(summary.criticalCount > 0 || summary.seriousCount > 0 || summary.moderateCount > 0) && (
        <div className="card bg-gray-light">
          <h3 className="font-semibold text-dark-navy mb-2">Top Findings Summary</h3>
          <p className="text-body-sm text-gray-dark">
            {[
              summary.criticalCount > 0 && `${summary.criticalCount} Critical`,
              summary.seriousCount > 0 && `${summary.seriousCount} Serious`,
              summary.moderateCount > 0 && `${summary.moderateCount} Moderate`,
            ]
              .filter(Boolean)
              .join(', ')}{' '}
            issues requiring attention
          </p>
        </div>
      )}
    </div>
  );
}
